import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Expand, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import Lightbox from "../components/Lightbox";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Common";
import { IMAGES, OUD_CHIP_GRADES, waLink } from "../mock/mock";

const chipsJsonLd = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Agarwood Oud Chips",
  image: IMAGES.oudChips,
  description: "High-quality natural agarwood oud chips harvested from the heartwood of the Aquilaria tree. Hand-sorted into five grades (Muri, Salla, Jurra, Kalagashi, Girmit) and three tiers (Triple Super, Double Super, Super).",
  brand: { "@type": "Brand", name: "Meilleur Oudh & Aromas" },
  category: "Agarwood / Oud Chips",
  offers: {
    "@type": "AggregateOffer",
    availability: "https://schema.org/InStock",
    priceCurrency: "USD",
    seller: { "@type": "Organization", name: "Meilleur Oudh & Aromas" },
  },
};

const OudChips = () => {
  const [box, setBox] = useState({ open: false, images: [], title: "", index: 0 });
  const openGallery = (grade) => setBox({ open: true, images: grade.gallery, title: `${grade.name} — Oud Chips`, index: 0 });

  return (
    <div className="bg-noir">
      <Seo
        title="Oud Chips — Graded Agarwood Chips (Muri, Salla, Jurra) | Meilleur"
        description="Premium agarwood oud chips, hand-sorted into five grades and three tiers — for incense, bakhoor and distillation. Grown on our own plantations in Assam. Worldwide export."
        path="/oud-chips"
        keywords="oud chips, agarwood chips, buy oud chips, Muri oud, Salla, Jurra, Kalagashi, Girmit, agarwood grades, oud chips supplier"
        jsonLd={chipsJsonLd}
      />
      <PageHero eyebrow="Meilleur Oudh & Aromas" title="Oud" accent="Chips" image={IMAGES.oudChips} crumbs={["Collections", "Oud Chips"]} />

      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <Reveal><SectionHeading eyebrow="From the Heartwood" title="Hand-sorted," accent="sun-dried" /></Reveal>
            <Reveal delay={120}>
              <div className="mt-8 space-y-6 text-[var(--ivory-muted)] text-lg leading-relaxed font-body font-light">
                <p>
                  High-quality, natural wood chips harvested from the heartwood of the Aquilaria tree — known for their
                  deep, warm and complex aroma, perfect for incense, diffusers and other aromatic products.
                </p>
                <p>
                  Each variety is hand-sorted into three quality tiers — <span className="text-[var(--gold-light)]">Triple
                  Super, Double Super and Super</span> — to suit different applications and markets across the Gulf and
                  Europe.
                </p>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-9 flex flex-wrap gap-3">
                {["Triple Super", "Double Super", "Super"].map((t) => (
                  <span key={t} className="border border-[var(--line)] px-5 py-2.5 text-xs tracking-[0.16em] uppercase text-[var(--gold-light)]">{t}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={140}>
              <div className="grid grid-cols-2 gap-4">
                <div className="img-zoom"><img src="/gallery/chip-02.jpg" alt="Real agarwood oud chips" className="w-full h-72 object-cover" /></div>
                <div className="img-zoom mt-8"><img src="/gallery/chip-06.jpg" alt="Sun-dried oud chips" className="w-full h-72 object-cover" /></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Grades */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="Graded Oud / Agarwood Chips" title="Five" accent="grades" align="center" /></Reveal>
          <div className="mt-16 space-y-px bg-[var(--line)] border border-[var(--line)]">
            {OUD_CHIP_GRADES.map((g, i) => (
              <Reveal key={g.name} delay={i * 70}>
                <div className="bg-noir group grid md:grid-cols-12 gap-6 items-center px-7 md:px-10 py-8 hover:bg-[rgba(20,81,60,0.04)] transition-colors">
                  <div className="md:col-span-1 font-serif-display text-3xl gold-text opacity-50 group-hover:opacity-100 transition-opacity">{String(i + 1).padStart(2, "0")}</div>
                  <div className="md:col-span-3">
                    <h3 className="font-serif-display text-3xl group-hover:text-[var(--gold-light)] transition-colors">{g.name}</h3>
                    <span className="text-xs tracking-[0.12em] uppercase text-[var(--gold-light)] mt-2 inline-block">{g.tiers}</span>
                  </div>
                  <div className="md:col-span-4">
                    <p className="text-[var(--ivory-muted)] font-body leading-relaxed">{g.character}</p>
                  </div>
                  <div className="md:col-span-4">
                    <button onClick={() => openGallery(g)} className="w-full flex items-center gap-2 group/gal" aria-label={`View ${g.name} gallery`}>
                      <div className="flex gap-1.5 flex-1">
                        {g.gallery.slice(0, 3).map((img, k) => (
                          <div key={k} className="relative flex-1 h-16 overflow-hidden border border-[var(--line)]">
                            <img src={img} alt={`${g.name} chip ${k + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover/gal:scale-110" />
                          </div>
                        ))}
                      </div>
                      <span className="shrink-0 w-16 h-16 border border-[var(--line)] flex flex-col items-center justify-center text-[var(--gold)] group-hover/gal:border-[var(--gold)] group-hover/gal:bg-[rgba(20,81,60,0.08)] transition-all">
                        <Expand size={16} />
                        <span className="text-[0.55rem] tracking-[0.1em] uppercase mt-1">Zoom</span>
                      </span>
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Uses */}
      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="Applications" title="Where our chips" accent="belong" /></Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              { t: "Incense & Bakhoor", d: "The raw material behind our signature hand-blended bakhoor and premium incense." },
              { t: "Diffusers & Ambiance", d: "Slow-releasing warmth for homes, lounges and hospitality spaces." },
              { t: "Distillation", d: "Feedstock for our 27 in-house distillation units, yielding precious oud oil." },
            ].map((u, i) => (
              <Reveal key={u.t} delay={i * 110}>
                <div className="lux-card p-9 h-full">
                  <Check size={22} className="text-[var(--gold)] mb-5" />
                  <h3 className="font-serif-display text-2xl mb-3">{u.t}</h3>
                  <p className="text-sm text-[var(--ivory-muted)] font-body leading-relaxed">{u.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="border border-[var(--line)] p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="font-serif-display text-3xl md:text-4xl">Enquire about <span className="gold-text italic">bulk grades</span></h2>
                <p className="text-[var(--ivory-muted)] mt-3 font-body font-light">Worldwide shipping · custom sorting on request.</p>
              </div>
              <div className="flex flex-wrap gap-4 shrink-0">
                <a href={waLink("Oud Chips")} target="_blank" rel="noopener noreferrer" className="btn-gold">
                  <MessageCircle size={15} /> WhatsApp Enquiry
                </a>
                <Link to="/contact" className="btn-ghost">Request a Quote <ArrowRight size={15} /></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Lightbox
        open={box.open}
        images={box.images}
        title={box.title}
        index={box.index}
        onClose={() => setBox({ ...box, open: false })}
      />
    </div>
  );
};

export default OudChips;
